import React, { useState, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { keyCode } from '../../helpers';
import { Icon } from '../';
import { useTooltip } from '../../hooks';

import './Button.css';

/**
 * A button. Renders a div by default, or a router link when given `to` — a button that navigates is
 * a link, which is what lets it be opened in a new tab and focused without the tabIndex and key
 * handler a div needs.
 *
 * @param {object} props
 * @param {string} [props.name]           - Name attribute, for a button inside a form.
 * @param {string|JSX.Element} [props.value] - The label. A node when part of it is styled apart,
 *                                          such as a count shown next to the words.
 * @param {string} [props.valueLoading]   - Label shown while an async onClick is in flight.
 * @param {string} [props.valueClicked]   - Label shown for a second after a successful onClick.
 * @param {function} [props.onClick]      - Click handler. A button without one is decoration.
 * @param {string} [props.to]             - Route to navigate to. Renders the button as a link.
 * @param {boolean} [props.disabled=false] - Greys the button out and ignores clicks.
 * @param {string} [props.className]      - Additional CSS class names.
 * @param {string} [props.tooltip]        - Tooltip shown on hover.
 * @param {string} [props.buttonStyle]    - Style variant class, e.g. 'main-button'.
 * @param {string} [props.icon]           - Icon rendered before the label.
 * @param {React.Ref} ref
 */
const _Button = (
  {
    name,
    value,
    valueLoading,
    valueClicked,
    onClick,
    to,
    disabled = false,
    className,
    tooltip,
    buttonStyle,
    icon,
  },
  ref
) => {
  const [loading, setLoading] = useState(false);
  const [clicked, setClicked] = useState(false);

  const { showTooltip, hideTooltip } = useTooltip();

  /**
   * Keyboard equivalent of a click: the button is a div, so Enter and Space have to be wired up.
   * @param {KeyboardEvent} e
   */
  const handleKeyDown = e => {
    switch (e.keyCode) {
      case keyCode.ENTER:
      case keyCode.SPACE:
        // Guarded like handleClick: a button rendered without an onClick is decoration, and calling
        // it threw as soon as the button was focused and Enter pressed.
        !disabled && onClick && onClick();
        break;

      default:
        break;
    }
  };

  const handleClick = async e => {
    if (!disabled && !loading && !clicked && onClick) {
      setLoading(true);
      try {
        await onClick(e);
        if (valueClicked) {
          setClicked(true);
          setTimeout(() => setClicked(false), 1000);
        }
      } catch {
        // assume errors are handled in onClick function
      } finally {
        setLoading(false);
      }
    }
  };

  /**
   * Click handler for the link rendering. An anchor has no disabled attribute, so a disabled one
   * navigates unless the click is stopped here.
   * @param {MouseEvent} e
   */
  const handleLinkClick = e => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    onClick && handleClick(e);
  };

  const classNames =
    'CustomButton button' +
    (className ? ' ' + className : '') +
    (disabled || loading || clicked ? ' disabled' : '') +
    (buttonStyle ? ' ' + buttonStyle : '') +
    (loading ? ' loading' : '') +
    (clicked ? ' clicked' : '');

  const label = loading && valueLoading ? valueLoading : clicked ? valueClicked : value;
  const hasLabel = Boolean(value || valueLoading || valueClicked);

  // An icon belonging to a label goes inside its span, which is the element the hover background is
  // painted on: beside it, the icon stays unlit and the button lights up in halves. A button with no
  // label is the icon alone, with no span to sit in.
  const content = hasLabel ? (
    <span>
      {icon && <Icon name={icon} tooltip={tooltip} />}
      {label}
    </span>
  ) : (
    icon && <Icon name={icon} tooltip={tooltip} />
  );

  // Focus and Enter come with the anchor, so the tabIndex and key handler below are left off.
  if (to) {
    return (
      <Link
        to={to}
        className={classNames}
        ref={ref}
        onMouseEnter={() => tooltip && showTooltip({ title: tooltip })}
        onMouseLeave={() => tooltip && hideTooltip()}
        onClick={handleLinkClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      className={classNames}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      ref={ref}
      onMouseEnter={() => tooltip && showTooltip({ title: tooltip })}
      onMouseLeave={() => tooltip && hideTooltip()}
      name={name}
      onClick={handleClick}
    >
      {content}
    </div>
  );
};

export const Button = forwardRef(_Button);
